/**
 * Public Transcript View Component
 * Displays a student transcript via a shareable link without requiring authentication
 */

import React, { useState, useEffect, Suspense } from 'react';
import { db } from './firebase';
import { 
  collection, 
  query, 
  where, 
  onSnapshot,
  orderBy
} from 'firebase/firestore';
import { handleGetShareableTranscript } from './dataHandlers';
import { TranscriptProjectsSection } from './TranscriptProjectsSection';
import { timeLoggingUtils } from './TimeLoggingComponents';
import ComponentLoader from './components/ComponentLoader';

const PublicTranscriptView = ({ token }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareableData, setShareableData] = useState(null);
  const [studentProfile, setStudentProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [tagFeedbacks, setTagFeedbacks] = useState([]);
  const [tags, setTags] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [timeLogs, setTimeLogs] = useState([]);
  const [studentClasses, setStudentClasses] = useState([]);
  const [classTypes, setClassTypes] = useState([]);
  const [graduationRequirements, setGraduationRequirements] = useState([]);
  const [allFeedbacksData, setAllFeedbacksData] = useState([]);

  // Update tagFeedbacks when posts change - filter to only feedback for visible posts (featured only in public view)
  useEffect(() => {
    if (!shareableData) return;
    
    if (allFeedbacksData.length > 0 && posts.length > 0) {
      // Get post IDs from posts that are not hidden from transcript and are featured (for public view)
      const visiblePostIds = posts
        .filter(post => 
          post.userId === shareableData.studentId && 
          !post.hideFromTranscript && 
          post.featured // Only featured posts in public view
        )
        .map(p => p.id);
      
      if (visiblePostIds.length > 0) {
        const filteredFeedbacks = allFeedbacksData.filter(tf => visiblePostIds.includes(tf.postId));
        setTagFeedbacks(filteredFeedbacks);
      } else {
        setTagFeedbacks([]);
      }
    } else if (allFeedbacksData.length === 0) {
      setTagFeedbacks([]);
    }
  }, [posts, allFeedbacksData, shareableData]);

  useEffect(() => {
    const loadPublicTranscript = async () => {
      if (!token) {
        setError('No shareable link token provided.');
        setLoading(false);
        return;
      }

      try {
        // Fetch the shareable transcript record
        const shareable = await handleGetShareableTranscript({ token });
        
        if (!shareable) {
          setError('This shareable link is invalid or has been revoked.');
          setLoading(false);
          return;
        }

        setShareableData(shareable);
        const studentId = shareable.studentId;

        // Fetch student profile - use direct document access
        const { doc: docFn, getDoc } = await import('firebase/firestore');
        const userDocRef = docFn(db, 'users', studentId);
        try {
          const userSnap = await getDoc(userDocRef);
          if (userSnap.exists()) {
            setStudentProfile({ id: userSnap.id, ...userSnap.data() });
          } else {
            console.error('Student profile not found for ID:', studentId);
            setError('Student profile not found.');
            setLoading(false);
            return;
          }
        } catch (profileError) {
          console.error('Error fetching student profile:', profileError);
          setError('Failed to load student profile. Please check the link and try again.');
          setLoading(false);
          return;
        }

        // Set up real-time listeners for transcript data
        // Posts - Load all posts, filtering for hideFromTranscript happens in TranscriptProjectsSection
        const postsQuery = query(
          collection(db, 'posts'),
          where('userId', '==', studentId),
          orderBy('timestamp', 'desc')
        );
        const unsubscribePosts = onSnapshot(
          postsQuery, 
          (snapshot) => {
            const postsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setPosts(postsData);
          },
          (error) => {
            console.error('Error loading posts:', error);
            // If orderBy fails due to missing index, try without orderBy
            if (error.code === 'failed-precondition') {
              const postsQueryNoOrder = query(
                collection(db, 'posts'),
                where('userId', '==', studentId)
              );
              const unsubscribePostsNoOrder = onSnapshot(
                postsQueryNoOrder, 
                (snapshot) => {
                  const postsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
                    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
                  setPosts(postsData);
                },
                (noOrderError) => {
                  console.error('Error loading posts (no order):', noOrderError);
                  setPosts([]); // Set empty array on error
                }
              );
              return () => unsubscribePostsNoOrder();
            } else {
              console.error('Posts query failed:', error.code, error.message);
              setPosts([]); // Set empty array on error
            }
          }
        );

        // Tag Feedback - subscribe to all feedback
        const tagFeedbackQuery = query(
          collection(db, 'tagFeedback'),
          orderBy('timestamp', 'asc')
        );
        const unsubscribeTagFeedback = onSnapshot(
          tagFeedbackQuery, 
          (snapshot) => {
            const feedbacksData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setAllFeedbacksData(feedbacksData);
          },
          (error) => {
            console.error('Error loading tag feedback:', error);
            setAllFeedbacksData([]); // Set empty array on error
          }
        );

        // Tags
        try {
          const { doc: tagsDoc, getDoc: getTagsDoc } = await import('firebase/firestore');
          const tagsDocRef = tagsDoc(db, 'settings', 'tags');
          const tagsSnap = await getTagsDoc(tagsDocRef);
          if (tagsSnap.exists()) {
            setTags(tagsSnap.data().list || []);
          } else {
            console.warn('Tags document not found, using empty array');
            setTags([]);
          }
        } catch (tagsError) {
          console.error('Error loading tags:', tagsError);
          setTags([]); // Set empty array on error
        }

        // Certificates
        const certificatesQuery = query(
          collection(db, 'certificates'),
          where('userId', '==', studentId)
        );
        const unsubscribeCertificates = onSnapshot(
          certificatesQuery, 
          (snapshot) => {
            setCertificates(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
          },
          (error) => {
            console.error('Error loading certificates:', error);
            setCertificates([]); // Set empty array on error
          }
        );

        // Time Logs - Load all time logs, filtering happens in TimeLogTranscriptDisplay
        const timeLogsQuery = query(
          collection(db, 'timeLogs'),
          where('userId', '==', studentId),
          orderBy('startDate', 'desc')
        );
        const unsubscribeTimeLogs = onSnapshot(
          timeLogsQuery, 
          (snapshot) => {
            const logsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setTimeLogs(logsData);
          },
          (error) => {
            console.error('Error loading time logs:', error);
            // If orderBy fails due to missing index, try without orderBy
            if (error.code === 'failed-precondition') {
              const timeLogsQueryNoOrder = query(
                collection(db, 'timeLogs'),
                where('userId', '==', studentId)
              );
              const unsubscribeTimeLogsNoOrder = onSnapshot(
                timeLogsQueryNoOrder, 
                (snapshot) => {
                  const logsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
                    .sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
                  setTimeLogs(logsData);
                },
                (noOrderError) => {
                  console.error('Error loading time logs (no order):', noOrderError);
                  setTimeLogs([]); // Set empty array on error
                }
              );
              return () => unsubscribeTimeLogsNoOrder();
            } else {
              console.error('Time logs query failed:', error.code, error.message);
              setTimeLogs([]); // Set empty array on error
            }
          }
        );

        // Student Classes
        const studentClassesQuery = query(
          collection(db, 'studentClasses'),
          where('studentId', '==', studentId)
        );
        const unsubscribeStudentClasses = onSnapshot(
          studentClassesQuery, 
          (snapshot) => {
            setStudentClasses(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
          },
          (error) => {
            console.error('Error loading student classes:', error);
            setStudentClasses([]); // Set empty array on error
          }
        );

        // Class Types
        const classTypesQuery = query(collection(db, 'classTypes'));
        const unsubscribeClassTypes = onSnapshot(
          classTypesQuery, 
          (snapshot) => {
            setClassTypes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
          },
          (error) => {
            console.error('Error loading class types:', error);
            setClassTypes([]); // Set empty array on error
          }
        );

        // Graduation Requirements
        const graduationRequirementsQuery = query(
          collection(db, 'graduationRequirements'),
          where('studentUid', '==', studentId)
        );
        const unsubscribeGraduationRequirements = onSnapshot(
          graduationRequirementsQuery, 
          (snapshot) => {
            setGraduationRequirements(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
          },
          (error) => {
            console.error('Error loading graduation requirements:', error);
            setGraduationRequirements([]); // Set empty array on error
          }
        );

        setLoading(false);

        // Cleanup function
        return () => {
          unsubscribePosts();
          unsubscribeTagFeedback();
          unsubscribeCertificates();
          unsubscribeTimeLogs();
          unsubscribeStudentClasses();
          unsubscribeClassTypes();
          unsubscribeGraduationRequirements();
        };
      } catch (err) {
        console.error('Error loading public transcript:', err);
        setError('Failed to load transcript. Please check the link and try again.');
        setLoading(false);
      }
    };

    loadPublicTranscript();
  }, [token]);

  if (loading) {
    return (
      <div className="app-container">
        <div className="post post-margin-bottom">
          <ComponentLoader />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app-container">
        <div className="post post-margin-bottom">
          <h2>Transcript Not Available</h2>
          <p className="no-posts">{error}</p>
        </div>
      </div>
    );
  }

  if (!studentProfile) {
    return (
      <div className="app-container">
        <div className="post post-margin-bottom">
          <h2>Transcript Not Available</h2>
          <p className="no-posts">Student profile not found.</p>
        </div>
      </div>
    );
  }

  // Construct full name from firstName and lastName
  const firstName = studentProfile.firstName || '';
  const lastName = studentProfile.lastName || '';
  const fullName = `${firstName} ${lastName}`.trim() || studentProfile.name || 'Student';

  return (
    <div className="app-container">
      <div className="post post-margin-bottom">
        <h1>Barefoot Technology Academy</h1>
        <br />
        <h2>{fullName}'s Unofficial Transcript</h2>
        <p className="transcript-description">
          This is a public view of the student's unofficial transcript.
        </p>
        
        <Suspense fallback={<ComponentLoader />}>
          <TranscriptProjectsSection
            studentId={shareableData.studentId}
            posts={posts}
            tagFeedbacks={tagFeedbacks}
            tags={tags}
            studentClasses={studentClasses}
            classTypes={classTypes}
            certificates={certificates}
            timeLogs={timeLogs}
            currentUser={{ uid: shareableData.studentId }} // Mock currentUser for component compatibility
            graduationRequirements={graduationRequirements}
            requirementTypes={{}} // Empty for public view
            consolidateTimeLogs={timeLoggingUtils.consolidateTimeLogs}
            groupTimeLogsByGrade={timeLoggingUtils.groupTimeLogsByGrade}
            showGraduationRequirements={false} // Don't show graduation requirements in public view
            showOnlyFeatured={true} // Only show featured projects in public view
          />
        </Suspense>
      </div>
    </div>
  );
};

export default PublicTranscriptView;

